import { type AgentDto, type RunDto, type RunStatus, TERMINAL_RUN_STATUSES } from '@af/shared';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AgentOrb } from '@/components/ui/agent-orb';
import { IconChevron } from '@/components/ui/icons';
import { StatusDot } from '@/components/ui/status-dot';
import { relTime, usd } from '@/lib/format';
import { type AgentLive, elapsed, type RunActivity, useNow } from '@/lib/live-agents';
import { friendlyTool, toolActivityKind } from '@/lib/tool-display';
import { cn } from '@/lib/utils';

const isTerminal = (s: string) => TERMINAL_RUN_STATUSES.has(s as RunStatus);
const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

type T = (k: string, o?: Record<string, unknown>) => string;

/** "Using Trello · Read card", "Writing a reply…", … */
export function activityText(
  t: T,
  live: Pick<AgentLive, 'status' | 'activity' | 'run'> & Partial<Pick<AgentLive, 'waitingOn'>>,
): string {
  if (live.run?.status === 'awaiting_human') return t('inspector.act.question');
  if (live.status === 'approval') return t('inspector.act.approval');
  if (live.run?.status === 'waiting_children')
    return live.waitingOn?.length
      ? t('inspector.act.waitingOn', { names: live.waitingOn.join(', ') })
      : t('inspector.act.waitingChildren');
  if (live.run?.status === 'queued') return t('inspector.act.queued');
  if (live.run?.status === 'scheduled') return t('inspector.act.scheduled');
  const a: RunActivity | null = live.activity;
  if (!a) return t('inspector.act.working');
  if (a.kind === 'tool') {
    const kind = toolActivityKind(a.name);
    if (kind !== 'system') return t(`companyActivity.active.${kind}`);
    const f = friendlyTool(a.name);
    return t('inspector.act.tool', { tool: f.source ? `${f.source} · ${f.label}` : f.label });
  }
  if (a.kind === 'delegating') return t('inspector.act.delegating', { role: a.role });
  if (a.kind === 'approval') return t('inspector.act.approval');
  return t(`inspector.act.${a.kind}`);
}

/** Where to look at a run: its conversation, else the run itself. */
export function RunLink({
  run,
  className,
  children,
}: {
  run: RunDto;
  className?: string;
  children: ReactNode;
}) {
  return run.conversationId ? (
    <Link to="/chat/$conversationId" params={{ conversationId: run.conversationId }} className={className}>
      {children}
    </Link>
  ) : (
    <Link to="/runs/$id" params={{ id: run.id }} className={className}>
      {children}
    </Link>
  );
}

function Section({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section>
      <div className="mb-2 flex items-baseline justify-between gap-2 text-sm">
        <h3 className="font-medium text-fg-2">{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Today in three plain numbers: spent (vs budget), finished, failed. */
export function TodayTiles({
  runs,
  spentUsd,
  budgetPct,
}: {
  runs: RunDto[];
  spentUsd: number;
  budgetPct: number | null;
}) {
  const { t } = useTranslation();
  const since = startOfToday();
  const endedToday = runs.filter((r) => r.endedAt && Date.parse(r.endedAt) >= since);
  const done = endedToday.filter((r) => r.status === 'completed').length;
  const failed = endedToday.filter((r) => r.status === 'failed').length;
  const tile = (label: string, value: ReactNode, sub: ReactNode, tone?: 'danger' | 'warn') => (
    <div className="px-3.5 py-3">
      <div className="text-xs text-muted">{label}</div>
      <div
        className={cn(
          'mt-0.5 text-[22px] font-semibold tracking-tight',
          tone === 'danger' && 'text-danger',
          tone === 'warn' && 'text-warn',
        )}
      >
        {value}
      </div>
      <div className="truncate text-2xs text-faint">{sub}</div>
    </div>
  );
  return (
    <div className="grid grid-cols-3 divide-x divide-border overflow-hidden rounded-xl border border-border bg-panel">
      {tile(
        t('inspector.today.spent'),
        usd(spentUsd, 2),
        budgetPct != null ? t('inspector.today.ofBudget', { pct: budgetPct }) : t('inspector.today.noBudget'),
        budgetPct != null && budgetPct >= 100
          ? 'danger'
          : budgetPct != null && budgetPct >= 80
            ? 'warn'
            : undefined,
      )}
      {tile(t('inspector.today.done'), done, t('inspector.today.doneSub'))}
      {tile(
        t('inspector.today.failed'),
        failed,
        failed ? t('inspector.today.failedSub') : t('inspector.today.allGood'),
        failed ? 'danger' : undefined,
      )}
    </div>
  );
}

/** Approvals and questions that wait for a human, with a direct way to answer them. */
export function NeedsYou({ live, approvalsLink = true }: { live: AgentLive[]; approvalsLink?: boolean }) {
  const { t } = useTranslation();
  const items = live.flatMap((l) => [
    ...l.pending.map((a) => ({
      key: a.id,
      agent: l.agent,
      text: t('inspector.needs.approval', { tool: friendlyTool(a.toolName).label }),
      sub: a.inputPreview,
      link: (
        <Link
          to="/approvals"
          className="shrink-0 rounded-md border border-warn/40 px-2.5 py-1 text-xs text-warn hover:bg-warn/10"
        >
          {t('inspector.needs.review')}
        </Link>
      ),
    })),
    ...(l.run?.status === 'awaiting_human' && l.run.question
      ? [
          {
            key: `q-${l.run.id}`,
            agent: l.agent,
            text: t('inspector.needs.question'),
            sub: l.run.question.text,
            link: (
              <RunLink
                run={l.run}
                className="shrink-0 rounded-md border border-warn/40 px-2.5 py-1 text-xs text-warn hover:bg-warn/10"
              >
                {t('inspector.needs.answer')}
              </RunLink>
            ),
          },
        ]
      : []),
    // Waiting for an approval we have no row for (e.g. no approval:read): still show it.
    ...(l.status === 'approval' && !l.pending.length && l.run && l.run.status !== 'awaiting_human'
      ? [
          {
            key: `w-${l.run.id}`,
            agent: l.agent,
            text: t('inspector.needs.waiting'),
            sub: activityText(t, l),
            link: (
              <RunLink
                run={l.run}
                className="shrink-0 rounded-md border border-warn/40 px-2.5 py-1 text-xs text-warn hover:bg-warn/10"
              >
                {t('inspector.needs.review')}
              </RunLink>
            ),
          },
        ]
      : []),
  ]);
  if (!items.length) return null;
  return (
    <Section
      title={t('inspector.needs.title', { count: items.length })}
      action={
        approvalsLink ? (
          <Link to="/approvals" className="text-xs text-accent hover:underline">
            {t('inspector.needs.inbox')}
          </Link>
        ) : null
      }
    >
      <ul className="overflow-hidden rounded-xl border border-warn/30 bg-warn/5">
        {items.map((i) => (
          <li
            key={i.key}
            className="flex items-center gap-3 border-b border-warn/20 px-3 py-2.5 last:border-b-0"
          >
            <AgentOrb seed={i.agent.id} size={24} avatar={i.agent.avatar} name={i.agent.name} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm">
                <span className="font-medium">{i.agent.name}</span> {i.text}
              </span>
              <span className="block truncate text-xs text-muted">{i.sub}</span>
            </span>
            {i.link}
          </li>
        ))}
      </ul>
    </Section>
  );
}

/** Agents that are busy right now: what they do, for how long, at what cost so far. */
export function WorkingNow({ live }: { live: AgentLive[] }) {
  const { t } = useTranslation();
  const working = live.filter((l) => l.status === 'running' && l.run && !isTerminal(l.run.status));
  const now = useNow(1000, working.length > 0);
  return (
    <Section title={t('inspector.working.title')}>
      {working.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border-strong px-3 py-3 text-sm text-muted">
          {t('inspector.working.none')}
        </p>
      ) : (
        <ul className="overflow-hidden rounded-xl border border-border">
          {working.map((l) => (
            <li key={l.agent.id} className="border-b border-border last:border-b-0">
              <RunLink run={l.run!} className="flex items-center gap-3 px-3 py-2.5 hover:bg-hover">
                <span className="relative">
                  <AgentOrb seed={l.agent.id} size={28} avatar={l.agent.avatar} name={l.agent.name} />
                  <StatusDot status="running" className="absolute -right-0.5 -bottom-0.5 ring-2 ring-panel" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{l.agent.name}</span>
                  <span className="block truncate text-xs text-claude">{activityText(t, l)}</span>
                </span>
                <span className="shrink-0 text-right">
                  <span className="block font-mono text-xs text-fg-2">
                    {elapsed(l.run!.startedAt ?? l.run!.createdAt, now)}
                  </span>
                  <span className="block font-mono text-2xs text-faint">{usd(l.run!.costUsd)}</span>
                </span>
              </RunLink>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/** Idle (and paused) agents as a compact row of orbs. */
export function IdleAgents({ live }: { live: AgentLive[] }) {
  const { t } = useTranslation();
  const idle = live.filter((l) => l.status === 'idle' || l.status === 'paused' || l.status === 'failed');
  if (!idle.length) return null;
  return (
    <Link
      to="/chat"
      className="flex items-center gap-3 rounded-xl border border-border px-3 py-2.5 text-sm text-muted hover:bg-hover"
    >
      <span className="flex -space-x-1.5">
        {idle.slice(0, 6).map((l) => (
          <span key={l.agent.id} title={l.agent.name} className="rounded-full ring-2 ring-panel">
            <AgentOrb seed={l.agent.id} size={20} avatar={l.agent.avatar} name={l.agent.name} />
          </span>
        ))}
      </span>
      <span className="flex-1">{t('inspector.idle', { count: idle.length })}</span>
      <IconChevron size={12} className="text-faint" />
    </Link>
  );
}

/** The last finished runs in plain words. */
export function RecentlyFinished({
  runs,
  agents,
  limit = 5,
}: {
  runs: RunDto[];
  agents: AgentDto[] | undefined;
  limit?: number;
}) {
  const { t } = useTranslation();
  const done = runs
    .filter((r) => isTerminal(r.status) && r.endedAt)
    .sort((a, b) => (b.endedAt ?? '').localeCompare(a.endedAt ?? ''))
    .slice(0, limit);
  return (
    <Section
      title={t('inspector.recent.title')}
      action={
        <Link to="/runs" className="text-xs text-accent hover:underline">
          {t('inspector.recent.all')}
        </Link>
      }
    >
      {done.length === 0 ? (
        <p className="text-sm text-muted">{t('inspector.noRuns')}</p>
      ) : (
        <ul className="overflow-hidden rounded-xl border border-border">
          {done.map((r) => {
            const a = agents?.find((x) => x.id === r.agentId);
            const failed = r.status === 'failed';
            return (
              <li key={r.id} className="border-b border-border last:border-b-0">
                <RunLink run={r} className="flex items-center gap-3 px-3 py-2 hover:bg-hover">
                  <AgentOrb seed={r.agentId} size={22} avatar={a?.avatar} name={a?.name} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">
                      <span className="font-medium">{a?.name ?? t('chats.unknownAgent')}</span>{' '}
                      <span className={failed ? 'text-danger' : 'text-fg-2'}>
                        {t(
                          `inspector.recent.${r.status === 'completed' ? 'done' : failed ? 'failed' : 'stopped'}`,
                          {
                            what: t(`inspector.mode.${r.mode}`),
                          },
                        )}
                      </span>
                    </span>
                    <span className="block truncate text-xs text-muted">
                      {failed && r.error ? r.error : `${relTime(r.endedAt)} · ${usd(r.costUsd)}`}
                    </span>
                  </span>
                </RunLink>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

/** One-sentence status of the org (or an agent) for the top of the panel. */
export function StatusHero({ live, halted }: { live: AgentLive[]; halted: boolean }) {
  const { t } = useTranslation();
  const working = live.filter((l) => l.status === 'running').length;
  const waiting = live.filter((l) => l.status === 'approval').length;
  if (halted)
    return (
      <p className="flex items-center gap-2 text-lg font-medium text-danger">
        <StatusDot tone="failed" /> {t('inspector.hero.halted')}
      </p>
    );
  return (
    <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-lg font-medium tracking-tight">
      <span className="inline-flex items-center gap-2">
        <StatusDot tone={working ? 'running' : 'idle'} />
        {t('inspector.hero.working', { count: working })}
      </span>
      {waiting ? (
        <span className="inline-flex items-center gap-2 text-warn">
          <StatusDot tone="approval" />
          {t('inspector.hero.waiting', { count: waiting })}
        </span>
      ) : null}
    </p>
  );
}
